/**
 * multipage-nav.ts — Pure page-navigation predicates for the multipage editor.
 *
 * Extracted from editor-multipage.ts in the
 * ts-unit-testability-seams-260623 Epic.
 *
 * Contains:
 *   - MultipageCallbackBindings interface
 *   - setMultipageCallbackBindings / _requireMultipageCbBindings
 *   - updatePageNavControls  (DOM state sync for prev/next/input controls)
 *   - goToPage               (range-clamp + render dispatch)
 *
 * Does NOT contain:
 *   - mountMultiPageScroll   (IntersectionObserver/WASM lifecycle — stays in editor-multipage.ts)
 *   - _wirePageControllers   (per-page drag/resize/draw controller wiring — stays in editor-multipage.ts)
 *   - fixupPageSizes         (async page-size resolution — stays in editor-multipage.ts)
 *   - Module-eval-time addEventListener bindings — stay in editor-multipage.ts
 *
 * This module is side-effect-free at import time: it imports `state` and `els`
 * but does NOT call any DOM methods at module evaluation (unlike editor-multipage.ts
 * which wires button event listeners at eval time). That makes it unit-testable
 * in a stub-DOM Node environment.
 */
import { state, els } from "./editor-state.js";
import { renderPage } from "./editor-render.js";
let _multipageCbBindings = null;
/**
 * Install the orchestrator callback. Must be called from editor.ts AFTER
 * `selectStruct` is defined and BEFORE the first `mountMultiPageScroll`
 * invocation. Re-callable for tests that need to swap bindings.
 */
export function setMultipageCallbackBindings(bindings) {
    _multipageCbBindings = bindings;
}
/**
 * Guard: assert bindings are installed before any per-page controller
 * is constructed. Throws a descriptive error if not — silent null would
 * route every selectStruct call into the void.
 */
export function _requireMultipageCbBindings() {
    if (_multipageCbBindings === null) {
        throw new Error("editor-multipage: callback bindings not installed — "
            + "editor.ts must call setMultipageCallbackBindings(...) before "
            + "the first mountMultiPageScroll() / _wirePageControllers() call.");
    }
    return _multipageCbBindings;
}
// ─── Page navigation helpers ─────────────────────────────────────────────────
/**
 * Sync toolbar + sidebar page-nav controls to current state.
 * No-ops when `state.pdfDoc` is null (called before PDF is loaded).
 */
export function updatePageNavControls() {
    if (!state.pdfDoc)
        return;
    const total = state.pdfDoc.numPages;
    const cur = state.currentPage;
    // Toolbar controls.
    els.prevPageBtn.disabled = (cur <= 1);
    els.nextPageBtn.disabled = (cur >= total);
    els.pageInput.value = String(cur);
    els.pageInput.max = String(total);
    els.toolbarPageTotal.textContent = String(total);
    // Sidebar mirror controls (issue 10).
    els.sbPrevPageBtn.disabled = (cur <= 1);
    els.sbNextPageBtn.disabled = (cur >= total);
    els.sbPageInput.value = String(cur);
    els.sbPageInput.max = String(total);
    els.sbPageTotal.textContent = String(total);
}
/**
 * Navigate to a page number, clamping to [1, total].
 * No-ops when `state.pdfDoc` is null.
 */
export async function goToPage(pageNum) {
    if (!state.pdfDoc)
        return;
    const total = state.pdfDoc.numPages;
    const target = Math.max(1, Math.min(total, pageNum));
    if (target === state.currentPage)
        return;
    await renderPage(target);
    updatePageNavControls();
}
